import * as THREE from 'three';

/**
 * A padlock beside something a student can move, showing whether it will.
 *
 * Locked is the default (Placeable): moving the skeleton was the thing that
 * kept happening by accident while a child reached for a bone. Tap the padlock
 * to unlock, put it where you want, tap again. Two looks, never a word:
 *
 * - **closed, blue** -- it stays where it is;
 * - **open, amber** -- your hands can move it.
 *
 * Drawn on a canvas rather than loaded, for the reason nothing in the headset
 * is loaded from outside (CSP `default-src 'self'`), and on a disc rather than
 * a Sprite because a ray has to be able to find it: three.js cannot raycast a
 * sprite without a camera, which is exactly the crash the bone labels caused.
 *
 * Turned to face the viewer every frame, and kept the same size in the world
 * however small the skeleton has been made -- a padlock shrunk with a skeleton
 * onto a desk would be too small to press.
 */

/** About the size of a large button, in metres. */
const DIAMETER = 0.09;

export class LockButton {
  readonly mesh: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  #locked = true;
  readonly #canvas = document.createElement('canvas');
  readonly #texture: THREE.CanvasTexture;
  #onToggle: ((locked: boolean) => void) | undefined;

  constructor(parent: THREE.Object3D, at: THREE.Vector3) {
    this.#canvas.width = 128;
    this.#canvas.height = 128;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.CircleGeometry(DIAMETER / 2, 32),
      new THREE.MeshBasicMaterial({ map: this.#texture, transparent: true }),
    );
    this.mesh.position.copy(at);
    this.mesh.renderOrder = 5;
    this.mesh.userData['lock'] = this;
    parent.add(this.mesh);
    this.#draw();
  }

  get locked(): boolean {
    return this.#locked;
  }

  onToggle(handler: (locked: boolean) => void): void {
    this.#onToggle = handler;
  }

  toggle(): void {
    this.#locked = !this.#locked;
    this.#draw();
    this.#onToggle?.(this.#locked);
  }

  set visible(visible: boolean) {
    this.mesh.visible = visible;
  }

  /** Once a frame: face the viewer, and stay the same size in the world. */
  update(camera: THREE.Camera): void {
    if (!this.mesh.visible) return;
    const eye = camera.getWorldPosition(new THREE.Vector3());
    this.mesh.lookAt(eye);

    const parentScale = this.mesh.parent?.getWorldScale(new THREE.Vector3()).x ?? 1;
    this.mesh.scale.setScalar(1 / Math.max(parentScale, 0.001));
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.#texture.dispose();
  }

  #draw(): void {
    const ctx = this.#canvas.getContext('2d')!;
    const size = this.#canvas.width;
    ctx.clearRect(0, 0, size, size);

    // The disc: the product's one blue for "stays put", amber for "will move".
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 3, 0, Math.PI * 2);
    ctx.fillStyle = this.#locked ? '#3b82f6' : '#f59e0b';
    ctx.fill();
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.stroke();

    // The shackle: closed into the body, or lifted and swung open.
    ctx.lineWidth = 10;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#ffffff';
    ctx.beginPath();
    if (this.#locked) {
      ctx.moveTo(44, 62);
      ctx.lineTo(44, 48);
      ctx.arc(64, 48, 20, Math.PI, 0);
      ctx.lineTo(84, 62);
    } else {
      ctx.moveTo(44, 62);
      ctx.lineTo(44, 40);
      ctx.arc(64, 40, 20, Math.PI, Math.PI * 1.95);
    }
    ctx.stroke();

    // The body.
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(34, 60, 60, 40, 8);
    ctx.fill();
    ctx.fillStyle = this.#locked ? '#3b82f6' : '#f59e0b';
    ctx.beginPath();
    ctx.arc(64, 78, 6, 0, Math.PI * 2);
    ctx.fill();

    this.#texture.needsUpdate = true;
  }
}
